import { useCallback, useEffect, useState } from 'react';
import { Scanner, type IDetectedBarcode, type IScannerError } from '@yudiel/react-qr-scanner';
import { Camera, X } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { parseOperatorQr, type QrParseResult } from '../model/nfce-qr';

interface OperatorQrScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (result: QrParseResult) => void;
}

/** Erros que realmente impedem o uso da câmera (não só constraint). */
const BLOCKING_ERROR_KINDS = new Set([
  'permission-denied',
  'no-camera',
  'in-use',
  'insecure-context',
]);

function scannerErrorMessage(err: IScannerError): string {
  switch (err.kind) {
    case 'permission-denied':
      return 'Permissão da câmera negada. Libere o acesso nas configurações do navegador.';
    case 'no-camera':
      return 'Nenhuma câmera encontrada neste dispositivo.';
    case 'in-use':
      return 'A câmera está em uso por outro aplicativo ou aba.';
    case 'insecure-context':
      return 'A câmera só funciona em HTTPS (ou localhost).';
    default:
      return err.message || 'Não foi possível iniciar o scanner.';
  }
}

export function OperatorQrScanner({ open, onClose, onScan }: OperatorQrScannerProps) {
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  /** Após overconstrained (ex.: Mac sem câmera traseira), remonta sem facingMode. */
  const [relaxedConstraints, setRelaxedConstraints] = useState(false);

  useEffect(() => {
    if (!open) {
      setError(null);
      setLocked(false);
      setRelaxedConstraints(false);
    }
  }, [open]);

  const handleResult = useCallback(
    (codes: IDetectedBarcode[]) => {
      if (locked) return;
      const raw = codes[0]?.rawValue?.trim();
      if (!raw) return;
      setLocked(true);
      setError(null);
      onScan(parseOperatorQr(raw));
      onClose();
    },
    [locked, onClose, onScan],
  );

  const handleError = useCallback((err: unknown) => {
    const scanned = err as IScannerError;
    const kind = scanned?.kind;

    // facingMode: environment falha em notebook — câmera ainda pode abrir; não assusta o operador.
    if (kind === 'overconstrained') {
      setRelaxedConstraints(true);
      setError(null);
      return;
    }

    if (kind && !BLOCKING_ERROR_KINDS.has(kind)) {
      // Falhas transitórias de detecção não devem esconder o vídeo.
      console.warn('[operator-qr]', kind, scanned?.message);
      return;
    }

    setError(scannerErrorMessage(scanned));
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background/95 backdrop-blur-md">
      <div className="flex items-center justify-between px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Camera className="size-4" />
          Escanear QR
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Fechar scanner">
          <X className="size-5" />
        </Button>
      </div>
      <p className="px-4 pb-3 text-sm text-muted-foreground">
        Aponte para o QR do cliente (CPF/wallet) ou da nota fiscal (NFC-e).
      </p>
      <div className="relative mx-4 min-h-0 flex-1 overflow-hidden rounded-2xl border border-[hsl(var(--glass-border)/0.5)]">
        <Scanner
          key={relaxedConstraints ? 'relaxed' : 'env'}
          onScan={handleResult}
          onError={handleError}
          formats={['qr_code']}
          sound={false}
          constraints={
            relaxedConstraints
              ? undefined
              : { facingMode: { ideal: 'environment' } }
          }
          styles={{
            container: { width: '100%', height: '100%' },
            video: { objectFit: 'cover' },
          }}
        />
      </div>
      {error ? <p className="px-4 py-3 text-sm text-destructive">{error}</p> : null}
      <div className="px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
        <Button type="button" variant="secondary" className="h-11 w-full" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
